import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";

// ─── CORE ─────────────────────────────────────────────────────────────
import { AppCtx } from './core/context';
import { CLIENT_POLL_INTERVAL, STAFF_POLL_INTERVAL, PLATFORM_CLASS, APP_PUBLIC_URL } from './core/config';
import { OrgBrandName } from './components/OrgBrand';
import { getBrandLogoSrc } from './core/brand-logo';
import { sb } from './core/db';
import { Auth, setSession, authHeaders, fetchSyncRevision } from './core/auth';
import { connectPortalSync, applySyncRevision } from './core/portal-sync';
import { DEFAULT_ORG, DEFAULT_ROLE_PERMISSIONS, configureKraya } from './core/constants';
import { mergeSeedDocTemplates } from './core/seed-doc-templates';
import { parseOrgJsonRows } from './core/org-json-settings';
// HARDCODED seed catalogs — prefer GET /bundles and GET /document-templates
// import { DEFAULT_BUNDLES, DEFAULT_DOC_TEMPLATES } from './core/constants';
import { INR, initials, today, uuid, logAuditFailure, notify, notifyCompanyUsers, staffUserIds, mapClient, mapInvoice, mapTask, mapTicket, mapPayment, mapSub, mapResp, mapNotif, mapCrossSellOpportunity, getIcon, getLatestSubmission, submissionNeedsReview, rollupClientBilling, normalizeInvoice, resolveInvoiceFinancials, routeFromNotification, notificationVisual, dismissNotification, isClientPortalRole } from './core/helpers';
import { NotificationRow } from './components/NotificationRow';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Toast } from './components/Toast';
import { SubmitOverlay } from './components/SubmitOverlay';
import { runSubmitting as runSubmittingImpl } from './core/run-submitting';
import { Icon, InlineIcon } from './components/Icon';
import { isNativeApp } from './core/platform';
import { isMobileClientUser, loadMobileClientBootstrap, hydrateMobileClientPortal } from './mobile/clientPortal';
import { registerNativeDeepLinkListener, getLaunchUrl } from './core/oauth-flow';
import { ViewDocumentModal } from './mobile/ViewDocumentModal';

// ─── AUTH ─────────────────────────────────────────────────────────────
import { LoginPageV2 } from './modules/auth/LoginPageV2';
import { ForgotPasswordPage } from './modules/auth/ForgotPasswordPage';
import { RegistrationPage } from './modules/auth/RegistrationPage';
import { AuthCallbackPage } from './modules/auth/AuthCallbackPage';
import { ChangePasswordModal } from './modules/auth/ChangePasswordModal';
import { DeleteAccountModal } from './modules/auth/DeleteAccountModal';
import { registerNativeOAuthListener } from './core/oauth-flow';
import { isOAuthCallbackUrl } from './core/platform';

// ─── DASHBOARD ────────────────────────────────────────────────────────
import { AdminDashboard } from './modules/dashboard/AdminDashboard';
import { ManagerDashboard } from './modules/dashboard/ManagerDashboard';
import { AnalyticsPage, BillingSummaryPage, EmployeeAnalyticsPage } from './modules/dashboard/AnalyticsPage';

// ─── CLIENTS ──────────────────────────────────────────────────────────
import { ClientsPage } from './modules/clients/ClientsPage';
import { CreateClientModal } from './modules/clients/CreateClientModal';
import { EditClientModal } from './modules/clients/EditClientModal';
import { ViewClientModal } from './modules/clients/ViewClientModal';

// ─── INVOICES ─────────────────────────────────────────────────────────
import { InvoicesPage } from './modules/invoices/InvoicesPage';
import { CreateInvoiceModal } from './modules/invoices/CreateInvoiceModal';
import { ViewInvoiceModal } from './modules/invoices/ViewInvoiceModal';
import { PayNowModal } from './modules/invoices/PayNowModal';

// ─── TASKS ────────────────────────────────────────────────────────────
import { TasksPage } from './modules/tasks/TasksPage';
import { ViewTaskModal } from './modules/tasks/ViewTaskModal';
import { AddTaskModal } from './modules/tasks/AddTaskModal';
import { EditTaskModal } from './modules/tasks/EditTaskModal';
import { RecurringTasksPage } from './modules/tasks/RecurringTasksPage';
import { ClientTaskSubmitModal, ReviewSubmissionModal } from './modules/tasks/ClientTaskSubmitModal';

// ─── PAYMENTS ─────────────────────────────────────────────────────────
import { PaymentsPage } from './modules/payments/PaymentsPage';
import { RecordPaymentModal } from './modules/payments/RecordPaymentModal';

// ─── EMPLOYEES ────────────────────────────────────────────────────────
import { EmployeesPage } from './modules/employees/EmployeesPage';
import { CreateEmployeeModal } from './modules/employees/CreateEmployeeModal';
import { EditEmployeeModal } from './modules/employees/EditEmployeeModal';
import { ResetPasswordModal } from './modules/employees/ResetPasswordModal';
import { EmpDashboard, EmpTasks, EmpClients } from './modules/employees/EmpDashboard';

// ─── DIRECTORS & TICKETS ──────────────────────────────────────────────
import { DirectorsPage } from './modules/directors/DirectorsPage';
import { TicketsPage } from './modules/tickets/TicketsPage';

// ─── SETTINGS ─────────────────────────────────────────────────────────
import { OrgSettings } from './modules/settings/OrgSettings';
import { BundleSettings } from './modules/settings/BundleSettings';
import { UserSettings } from './modules/settings/UserSettings';
import { DocTemplatesSettings } from './modules/settings/DocTemplatesSettings';
import { CustomFieldsSettings } from './modules/settings/CustomFieldsSettings';
import { KrayaSettings } from './modules/settings/KrayaSettings';
import { ZohoWorkdriveSettings } from './modules/settings/ZohoWorkdriveSettings';
import { AppShellShimmer } from './components/AppShellShimmer';

// ─── CLIENT PORTAL ────────────────────────────────────────────────────
import { ClientHome } from './modules/client-portal/ClientHome';
import { ClientTasksV2 } from './modules/client-portal/ClientTasks';
import { ClientInvoices } from './modules/client-portal/ClientInvoices';
import { ClientDocs } from './modules/client-portal/ClientDocs';
import { RequestCallbackModal } from './modules/client-portal/RequestCallbackModal';

// ─── MARKETPLACE ──────────────────────────────────────────────────────
import { MarketplacePage } from './modules/marketplace/MarketplacePage';
import { FreeGiftsPage } from './modules/marketplace/FreeGiftsPage';
import { CrossSellAlert, CrossSellPage } from './modules/marketplace/CrossSellPage';

// ─── NOTIFICATIONS & REPORTS ──────────────────────────────────────────
import { NotificationsPage } from './modules/notifications/NotificationsPage';
import DeleteRequestsPage from './modules/delete-requests/DeleteRequestsPage';
import { ReportsPage } from './modules/reports/ReportsPage';
import { PolicyPage } from './modules/reports/PolicyPage';
import { DeleteAccountPage } from './modules/reports/DeleteAccountPage';
import { isPoliciesPath, isDeleteAccountPath, policiesTabFromLocation, policiesUrl, POLICIES_PATH } from './core/app-routes';

// ─── EXTERNAL ─────────────────────────────────────────────────────────
import { createAuthFor, syncAuthPassword } from './api';
import { IconProperties } from './icons.properties';

function AppV2() {
  // ─── Restore session from localStorage ───────────────────────────
  const getSavedUser = () => {
    try { return JSON.parse(localStorage.getItem("fb_user") || "null"); } catch { return null; }
  };
  const getSavedView = (role) => {
    const saved = localStorage.getItem("fb_view");
    if (saved) return saved;
    const portal = isClientPortalRole(role) ? "client-home" : null;
    return portal || { admin: "dashboard", manager: "dashboard", employee: "emp-dashboard", client: "client-home", director: "client-home" }[role] || "dashboard";
  };

  const [user, setUser] = useState(getSavedUser);
  const [view, setViewRaw] = useState(() => {
    if (isDeleteAccountPath()) return '';
    if (isPoliciesPath()) return 'policies';
    const u = getSavedUser();
    return u ? getSavedView(u.role) : "";
  });
  const [pathname, setPathname] = useState(() => window.location.pathname);
  const [toast, setToast] = useState(null);
  const [submitOverlay, setSubmitOverlay] = useState(null);
  const [modal, setModal] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const [showForgot, setShowForgot] = useState(false);
  const [showRegister, setShowRegister] = useState(() => {
    // Block self-service registration entry point on native (Apple Guideline 3.1.1)
    if (isNativeApp()) return false;
    try { return new URLSearchParams(window.location.search).get('register') === '1'; } catch { return false; }
  });
  const [authCallbackKey, setAuthCallbackKey] = useState(0);
  const [deepLinkParams, setDeepLinkParams] = useState(null);
  const [dbReady, setDbReady] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // ─── Core data ────────────────────────────────────────────────────
  const [org, setOrg] = useState(DEFAULT_ORG);
  const [logoError, setLogoError] = useState(false);
  // Start empty — populated from GET /bundles and GET /document-templates
  // const [bundles, setBundles] = useState(DEFAULT_BUNDLES);
  // const [docTpls, setDocTpls] = useState(DEFAULT_DOC_TEMPLATES);
  const [bundles, setBundles] = useState([]);
  const [docTpls, setDocTpls] = useState({});
  const [clients, setClients] = useState([]);
  const [employees, setEmps] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [notifications, setNotifs] = useState([]);
  const [notifFocus, setNotifFocus] = useState(null);
  const [payments, setPayments] = useState([]);
  const [submissions, setSubs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [appRoles, setAppRoles] = useState([]);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [userLinks, setUserLinks] = useState([]);            // #12 user_companies junction rows
  const [crossSellOpps, setCrossSellOpps] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(null); // #12 null = "All Companies"
  const [dbLoading, setDbLoading] = useState(false);
  const orgLoadedRef = useRef(false);
  const loadAllState = useRef({ inflight: null, pending: false });
  const syncRevisionRef = useRef(null);

  const refreshNotifications = useCallback(async (uid) => {
    if (!uid) return;
    try {
      const notifsRaw = await sb.select("notifications", `user_id=eq.${uid}`);
      setNotifs((Array.isArray(notifsRaw) ? notifsRaw : []).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).map(mapNotif));
    } catch { /* offline blip */ }
  }, []);

  const showToast = (msg, type = "info") => setToast({ msg, type });
  const runSubmitting = useCallback(
    (label, fn, kind) => runSubmittingImpl(setSubmitOverlay, label, fn, kind),
    [],
  );
  const openModal = (id, data = {}) => setModal({ id, data });
  const closeModal = () => setModal(null);

  useEffect(() => {
    const onViewDocument = (e) => {
      if (e.detail?.doc) openModal('view-document', e.detail);
    };
    window.addEventListener('fb-view-document', onViewDocument);
    return () => window.removeEventListener('fb-view-document', onViewDocument);
  }, []);

  useEffect(() => {
    configureKraya({
      automationEnabled: org.whatsapp_automation_enabled !== false && org.whatsapp_automation_enabled !== 'false',
    });
  }, [org.whatsapp_automation_enabled]);

  // Persist view to localStorage; keep /policies in the address bar for the public policy URL
  const setView = (v) => {
    setViewRaw(v);
    localStorage.setItem("fb_view", v);
    try {
      if (v === 'policies') {
        window.history.pushState({}, '', policiesUrl(policiesTabFromLocation()));
        setPathname(POLICIES_PATH);
      } else if (isPoliciesPath()) {
        window.history.replaceState({}, '', '/');
        setPathname('/');
      }
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    const onPop = () => {
      const p = window.location.pathname;
      setPathname(p);
      if (isDeleteAccountPath(p)) return;
      if (isPoliciesPath(p)) setViewRaw('policies');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (user?.mustChangePassword && modal?.id !== "force-change-password") {
      openModal("force-change-password");
    }
  }, [user?.id, user?.mustChangePassword]);

  // Reset logo error when URL changes
  useEffect(() => {
    setLogoError(false);
  }, [org?.logoUrl]);

  // Deep link / notification route: ?view=client-tasks&taskId=... (Capacitor WebView + mobile)
  useEffect(() => {
    let removeListener = () => {};

    const handleUrl = (url) => {
      if (!url) return;
      if (url.includes('auth/callback')) {
        const tail = url.split('auth/callback')[1] || '';
        window.history.replaceState({}, '', `/auth/callback${tail}`);
        setAuthCallbackKey((k) => k + 1);
      } else {
        // Parse non-OAuth deep links (e.g. ?view=tickets&ticketId=...)
        try {
          const u = new URL(url.replace('com.foundersbridge.app://', 'http://localhost/'));
          if (u.search) {
            const params = new URLSearchParams(u.search);
            setDeepLinkParams(Object.fromEntries(params.entries()));
            window.history.replaceState({}, '', u.search);
          }
        } catch (_) { /* ignore malformed */ }
      }
    };

    // 1. Check for launch URL (if app was started by a deep link)
    getLaunchUrl().then(handleUrl);

    // 2. Listen for subsequent deep links while app is open
    registerNativeDeepLinkListener(handleUrl).then((remove) => { removeListener = remove; });

    return () => removeListener();
  }, []);

  // Supabase sometimes redirects to Site URL root (?code=…) instead of /auth/callback
  useEffect(() => {
    if (user) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.get('code')) return;
    if (window.location.pathname === '/auth/callback') return;
    window.history.replaceState({}, '', `/auth/callback${window.location.search}${window.location.hash}`);
    setAuthCallbackKey((k) => k + 1);
  }, [user]);

  useEffect(() => {
    if (!user || !dbReady) return;

    // Merge URL params and deep link params
    const urlParams = new URLSearchParams(window.location.search);
    const params = {
      ...Object.fromEntries(urlParams.entries()),
      ...(deepLinkParams || {}),
    };

    const viewParam = params.view;
    const taskId = params.taskId;
    const ticketId = params.ticketId;
    const clientId = params.clientId;
    const invoiceId = params.invoiceId;

    if (viewParam) setView(viewParam);
    if (taskId) {
      if (user.role === "client") setView("client-tasks");
      else setView("tasks");

      const task = tasks.find(t => t.id === taskId);
      if (task && user.role === "client") {
        openModal("client-submit-task", { task });
      } else if (task) {
        const sub = getLatestSubmission(submissions, taskId);
        if (submissionNeedsReview(sub)) openModal("review-submission", { submissionId: sub.id });
      }
    }
    if (ticketId) {
      setView("tickets");
      setNotifFocus({ ticketId });
    }
    if (clientId) {
      if (user.role === "client") setView("client-home");
      else setView("clients");
      setNotifFocus({ clientId });
    }
    if (invoiceId) {
      if (user.role === "client") setView("client-invoices");
      else setView("invoices");

      const invoice = invoices.find(i => i.id === invoiceId);
      if (invoice) openModal("view-invoice", { invoice });
    }

    if (deepLinkParams) setDeepLinkParams(null); // Clear once handled

    if (!user && urlParams.get('register') === '1' && !isNativeApp()) {
      setShowRegister(true);
    }
  }, [user, dbReady, tasks, submissions, authCallbackKey, deepLinkParams]);

  // ─── Load data from Supabase ──────────────────────────────────────
  const loadAll = async (currentUser) => {
    if (!currentUser) return;
    if (loadAllState.current.inflight) {
      loadAllState.current.pending = true;
      return loadAllState.current.inflight;
    }

    loadAllState.current.inflight = (async () => {
    try {
      const mobileClient = isMobileClientUser(currentUser);

      if (mobileClient) {
        const { scopedCompanyIds } = await loadMobileClientBootstrap(currentUser, {
          setUserLinks,
          setClients,
          setAllUsers,
          setEmps,
          setNotifs,
          setDbReady,
        });
        hydrateMobileClientPortal(scopedCompanyIds, {
          setPaymentSchedule,
          setPayments,
          setSubs,
          setTasks,
          setInvoices,
        });
        setTickets([]);
        setCrossSellOpps([]);
        setAppRoles([]);

        if (!orgLoadedRef.current) {
          Promise.all([
            sb.getOrganisationSettings().catch(() => null),
            sb.getOrganisationKraya().catch(() => ({})),
            sb.select('org_settings').catch(() => []),
          ]).then(([settings, kraya, orgJsonRows]) => {
            const jsonSettings = parseOrgJsonRows(Array.isArray(orgJsonRows) ? orgJsonRows : []);
            if (settings && Object.keys(settings).length > 0) {
              setOrg((prev) => ({
                ...prev,
                ...settings,
                ...kraya,
                ...jsonSettings,
                gstRate: Number(settings.gstRate) || 18,
              }));
              orgLoadedRef.current = true;
            } else if (kraya && (kraya.configured || kraya.whatsapp_automation_enabled !== undefined)) {
              setOrg((prev) => ({ ...prev, ...kraya, ...jsonSettings }));
              orgLoadedRef.current = true;
            } else if (jsonSettings.customReports?.length || jsonSettings.marketplaceCatalogs?.length) {
              setOrg((prev) => ({ ...prev, ...jsonSettings }));
              orgLoadedRef.current = true;
            }
          }).catch(() => {});
        }

        Promise.all([
          sb.listBundles().catch(() => []),
          sb.listDocTemplates().catch(() => ({})),
        ]).then(([bundles, docTpls]) => {
          if (Array.isArray(bundles) && bundles.length > 0) setBundles(bundles);
          else setBundles([]);
          try {
            setDocTpls(mergeSeedDocTemplates(docTpls && typeof docTpls === 'object' ? docTpls : {}));
          } catch { /* seed defaults */ }
        }).catch(() => {});

        try {
          const rev = await fetchSyncRevision();
          if (rev != null) syncRevisionRef.current = rev;
        } catch { /* optional */ }
        return;
      }

      /** ponytail: batch size 4 — avoids 12 parallel Cosmos reads spiking RU/s on free tier */
      const batchRun = async (fns, size = 4) => {
        const out = [];
        for (let i = 0; i < fns.length; i += size) {
          out.push(...await Promise.all(fns.slice(i, i + size).map((fn) => fn())));
        }
        return out;
      };
      let [usersRaw, companiesRaw, invoicesRaw, tasksRaw,
        paymentsRaw, subsRaw, ticketsRaw, ticketRespRaw, scheduleRaw, linksRaw,
        bundlesRaw, docTplsMap] = await batchRun([
          () => sb.select("users"),
          () => sb.select("companies"),
          () => sb.select("invoices"),
          () => sb.select("tasks"),
          () => sb.select("payments"),
          () => sb.select("submissions"),
          () => sb.select("tickets"),
          () => sb.select("ticket_responses"),
          () => sb.select("payment_schedule"),
          () => sb.select("user_companies"),
          () => sb.listBundles().catch(() => []),
          () => sb.listDocTemplates().catch(() => ({})),
        ]);
      let scopedCompanyIds = null;
      if (isClientPortalRole(currentUser.role)) {
        scopedCompanyIds = new Set(
          (Array.isArray(linksRaw) ? linksRaw : [])
            .filter(l => l.user_id === currentUser.id)
            .map(l => l.company_id)
        );
        // Primary contact: user row shares id with company (Create Client & Set Login)
        if ((companiesRaw || []).some(c => c.id === currentUser.id)) {
          scopedCompanyIds.add(currentUser.id);
        }
      } else if (currentUser.role === "employee") {
        scopedCompanyIds = new Set(
          (companiesRaw || []).filter(c => c.assigned_to === currentUser.id).map(c => c.id)
        );
      }

      const scopedLinks = !scopedCompanyIds
        ? (linksRaw || [])
        : isClientPortalRole(currentUser.role)
          ? (linksRaw || []).filter(l => l.user_id === currentUser.id)
          : (linksRaw || []).filter(l => scopedCompanyIds.has(l.company_id));
      setUserLinks(Array.isArray(scopedLinks) ? scopedLinks : []);  // #12 director→company links

      const inScope = (row) => {
        if (!scopedCompanyIds) return true;
        const cid = row.company_id || row.client_id || row.id;
        return scopedCompanyIds.has(cid);
      };

      const scopedUsers = scopedCompanyIds
        ? (usersRaw || []).filter(u => {
            if (u.id === currentUser.id) return true;
            if (["admin", "manager", "employee", "sales"].includes(u.role)) {
              return currentUser.role === "employee";
            }
            if (u.role === "client" || u.role === "director") {
              if (scopedCompanyIds.has(u.id)) return true;
              return scopedLinks.some(l => l.user_id === u.id && scopedCompanyIds.has(l.company_id));
            }
            return false;
          })
        : (usersRaw || []);
      const scopedCompanies = scopedCompanyIds
        ? (companiesRaw || []).filter(c => scopedCompanyIds.has(c.id))
        : (companiesRaw || []);
      const scopedTasks = scopedCompanyIds ? (tasksRaw || []).filter(inScope) : (tasksRaw || []);
      const scopedInvoices = scopedCompanyIds ? (invoicesRaw || []).filter(inScope) : (invoicesRaw || []);
      const scopedPayments = scopedCompanyIds ? (paymentsRaw || []).filter(inScope) : (paymentsRaw || []);
      const scopedSubs = scopedCompanyIds ? (subsRaw || []).filter(inScope) : (subsRaw || []);
      const scopedTickets = scopedCompanyIds ? (ticketsRaw || []).filter(inScope) : (ticketsRaw || []);
      const scopedSchedule = scopedCompanyIds ? (scheduleRaw || []).filter(inScope) : (scheduleRaw || []);
      // #5 Load this user's notifications
      try {
        const notifsRaw = await sb.select("notifications", `user_id=eq.${currentUser.id}`);
        setNotifs((Array.isArray(notifsRaw) ? notifsRaw : []).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).map(mapNotif));
      } catch { }

      // Check we got a valid response (even empty array is valid)
      if (!Array.isArray(scopedUsers)) {
        console.warn("DB not reachable — no valid response");
        setDbReady(false);
        return;
      }

      // Payment schedule (milestones/instalments)
      setPaymentSchedule((Array.isArray(scopedSchedule) ? scopedSchedule : []).map(r => ({
        id: r.id, invoiceId: r.invoice_id, companyId: r.company_id, seq: r.seq || 1,
        label: r.label, amount: Number(r.amount) || 0, dueDate: r.due_date,
        triggerEvent: r.trigger_event, status: r.status || "unpaid", paidAmount: Number(r.paid_amount) || 0,
      })));

      // ── Users → employees list + update login cache ───────────────
      setAllUsers(scopedUsers || []);  // store ALL users for duplicate checks
      const empList = scopedUsers
        .filter(u => u.role === "employee" || u.role === "manager" || u.role === "admin" || u.role === "sales")
        .map(u => ({
          id: u.id, name: u.name, role: u.role,
          avatar: u.avatar || initials(u.name),
          color: u.color || "#0B1F3A",
          email: u.email, phone: u.phone,
          permissions: u.permissions || {},
        }));
      setEmps(empList);

      // Load access roles (for permission enforcement)
      const rolesRaw = await sb.listRoles().catch(() => []);
      setAppRoles(Array.isArray(rolesRaw) ? rolesRaw : []);

      // ── Invoices + line items (embedded on GET /invoices) ───────────
      const paymentsList = (scopedPayments || []).map(mapPayment);
      const invList = (scopedInvoices || []).map((raw) => {
        const inv = mapInvoice(raw);
        const lines = raw.line_items || [];
        inv.lineItems = lines
          .sort((a, b) => (a.seq || 0) - (b.seq || 0))
          .map(l => ({
            id: l.id, name: l.name, type: l.type, sac: l.sac,
            qty: l.qty || 1, unitPrice: Number(l.unit_price) || 0,
            gst: l.gst, amount: Number(l.amount) || 0,
          }));
        return resolveInvoiceFinancials(inv, paymentsList);
      });
      setInvoices(invList);

      // ── Tasks (filter by assignment for employee) ─────────────────
      let taskList = (scopedTasks || []).map(mapTask);
      if (currentUser.role === "employee") {
        taskList = taskList.filter(t => t.assignedTo === currentUser.id);
      }
      setTasks(taskList);

      // ── Companies → clients (with dynamic roll-ups) ────────────────
      let clientList = (scopedCompanies || []).map(comp => {
        const clientInvoices = invList.filter(inv => inv.clientId === comp.id && inv.status !== "void");
        const totalBilling = clientInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
        const collected = clientInvoices.reduce((sum, inv) => sum + (inv.paid || 0), 0);
        const pending = clientInvoices.reduce((sum, inv) => {
          const p = Number(inv.pending);
          return sum + (Number.isFinite(p) ? p : Math.max(0, (inv.total || 0) - (inv.paid || 0)));
        }, 0);

        const clientTasks = (scopedTasks || []).filter(t => t.company_id === comp.id);
        const doneTasks = clientTasks.filter(t => t.status === "done" || t.status === "approved" || t.status === "Approved" || t.status === "Done").length;
        const progress = clientTasks.length ? Math.round(doneTasks / clientTasks.length * 100) : 0;

        const mapped = mapClient(comp);
        return {
          ...mapped,
          totalBilling,
          collected,
          pending,
          progress
        };
      });
      if (currentUser.role === "employee") {
        clientList = clientList.filter(c => c.assignedTo === currentUser.id);
      }
      setClients(clientList);

      // ── Payments ──────────────────────────────────────────────────
      setPayments(paymentsList);

      // ── Submissions ───────────────────────────────────────────────
      setSubs((scopedSubs || []).map(mapSub));

      // ── Tickets + responses ───────────────────────────────────────
      const tktList = (scopedTickets || []).map(mapTicket);
      tktList.forEach(t => {
        t.responses = (ticketRespRaw || [])
          .filter(r => r.ticket_id === t.id)
          .map(mapResp);
      });
      let ticketList = tktList;
      if (isClientPortalRole(currentUser.role)) {
        const myCompIds = clientList.map(c => c.id);
        ticketList = tktList.filter(t => t.raisedBy === currentUser.id || myCompIds.includes(t.clientId));
      } else if (currentUser.role === "employee") {
        ticketList = tktList.filter(t =>
          t.assignedTo === currentUser.id || t.raisedBy === currentUser.id
        );
      }
      // Client sees their own tickets
      if (isClientPortalRole(currentUser.role)) {
        const myClientIds = clientList.map(c => c.id);
        ticketList = tktList.filter(t =>
          myClientIds.includes(t.clientId) ||
          t.raisedBy === currentUser.id ||
          t.raisedBy === currentUser.email  // fallback
        );
      }
      setTickets(ticketList);

      // Core portal data loaded — show Live even if optional sections below fail
      setDbReady(true);

      try {
        const crossSellRaw = await sb.select("cross_sell_opportunities");
        setCrossSellOpps((Array.isArray(crossSellRaw) ? crossSellRaw : []).map(mapCrossSellOpportunity));
      } catch { setCrossSellOpps([]); }

      // ── Org settings (once per session — avoids repeat Cosmos reads every poll) ──
      if (!orgLoadedRef.current) {
        try {
          const [settings, kraya, orgJsonRows] = await Promise.all([
            sb.getOrganisationSettings().catch(() => null),
            sb.getOrganisationKraya().catch(() => ({})),
            sb.select("org_settings").catch(() => []),
          ]);
          const jsonSettings = parseOrgJsonRows(Array.isArray(orgJsonRows) ? orgJsonRows : []);
          if (settings && Object.keys(settings).length > 0) {
            setOrg(prev => ({
              ...prev,
              ...settings,
              ...kraya,
              ...jsonSettings,
              gstRate: Number(settings.gstRate) || 18,
            }));
            orgLoadedRef.current = true;
          } else if (kraya && (kraya.configured || kraya.whatsapp_automation_enabled !== undefined)) {
            setOrg(prev => ({ ...prev, ...kraya, ...jsonSettings }));
            orgLoadedRef.current = true;
          } else if (jsonSettings.customReports?.length || jsonSettings.marketplaceCatalogs?.length) {
            setOrg(prev => ({ ...prev, ...jsonSettings }));
            orgLoadedRef.current = true;
          }
        } catch (_) { /* fallback below */ }
      }

      // Load bundles from GET /bundles (already UI-shaped)
      if (Array.isArray(bundlesRaw) && bundlesRaw.length > 0) {
        setBundles(bundlesRaw);
      } else {
        setBundles([]);
      }

      // Load document templates from GET /document-templates
      try {
        setDocTpls(mergeSeedDocTemplates(docTplsMap && typeof docTplsMap === "object" ? docTplsMap : {}));
      } catch { /* seed defaults apply on next read */ }

      try {
        const rev = await fetchSyncRevision();
        if (rev != null) syncRevisionRef.current = rev;
      } catch { /* optional */ }

    } catch (e) {
      console.warn("❌ DB load error:", e.message);
      setDbReady(false);
    }
    })();

    try {
      return await loadAllState.current.inflight;
    } finally {
      loadAllState.current.inflight = null;
      if (loadAllState.current.pending) {
        loadAllState.current.pending = false;
        loadAll(currentUser);
      }
    }
  };

  // ─── Refresh on login, save, or tab return ───────────────────────
  useEffect(() => {
    if (!user) return;
    const onSessionExpired = () => {
      showToast('Session expired. Please sign in again.', 'error');
      logout();
    };
    window.addEventListener('fb-session-expired', onSessionExpired);
    return () => window.removeEventListener('fb-session-expired', onSessionExpired);
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    loadAll(user);
    let hiddenAt = null;
    let reloadTimer = null;
    const onReload = () => {
      refreshNotifications(user.id);
      clearTimeout(reloadTimer);
      reloadTimer = setTimeout(() => loadAll(user), 80);
    };

    // Cross-user sync: SSE push (instant) + revision poll fallback if stream drops
    const pollMs = isClientPortalRole(user.role) ? CLIENT_POLL_INTERVAL : STAFF_POLL_INTERVAL;
    const syncFromServer = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        applySyncRevision(await fetchSyncRevision(), syncRevisionRef, onReload);
      } catch { /* offline / auth blip */ }
    };

    const disconnectSse = connectPortalSync({
      syncRevisionRef,
      onReload,
      onNotifications: () => refreshNotifications(user.id),
    });

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
      } else {
        syncFromServer();
        if (hiddenAt && Date.now() - hiddenAt > 120000) {
          loadAll(user);
          hiddenAt = null;
        }
      }
    };

    // Hard refresh + bfcache restore: always pull latest from DB
    const onPageShow = (e) => {
      if (e.persisted) loadAll(user);
    };

    window.addEventListener("fb-reload", onReload);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);

    syncFromServer();
    const syncTimer = setInterval(syncFromServer, pollMs);

    return () => {
      clearTimeout(reloadTimer);
      clearInterval(syncTimer);
      disconnectSse();
      window.removeEventListener("fb-reload", onReload);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [user?.id, refreshNotifications]);

  // ─── Login / Logout ───────────────────────────────────────────────
  const handleLogin = async (u) => {
    const withFlags = { ...u, mustChangePassword: !!(u.mustChangePassword || u.must_change_password) };
    localStorage.setItem("fb_user", JSON.stringify(withFlags));
    setUser(withFlags);
    if (withFlags.mustChangePassword) {
      openModal("force-change-password");
      return;
    }
    const defaultView = { admin: "dashboard", manager: "dashboard", employee: "emp-dashboard", client: "client-home", director: "client-home" }[withFlags.role] || "dashboard";
    setView(defaultView);
  };

  const logout = async () => {
    await Auth.logout();
    setSession(null);
    localStorage.removeItem("fb_user");
    localStorage.removeItem("fb_view");
    setUser(null); setViewRaw("");
    setClients([]); setTasks([]);
    setInvoices([]); setTickets([]);
    setPayments([]); setSubs([]);
    setEmps([]);
  };

  // ─── DB write helpers ─────────────────────────────────────────────
  const persistTaskUpdate = async (task) => {
    await sb.update("tasks", "id", task.id, {
      status: task.status, notes: task.notes || "", assigned_to: task.assignedTo || null,
      due_date: task.dueDate || null, completed_date: task.completedDate || null,
      updated_at: new Date().toISOString(),
    });
  };

  const persistTicketUpdate = async (ticket) => {
    await sb.update("tickets", "id", ticket.id, {
      status: ticket.status, tat: ticket.tat || null, updated_at: new Date().toISOString(),
    });
  };

  const dbCreateClient = async (clientData) => {
    setClients(cs => [...cs, clientData]); // optimistic
    try {
      await sb.insert("companies", {
        id: clientData.id, client_no: clientData.clientNo,
        name: clientData.name, contact_name: clientData.contactName || "",
        email: clientData.email, phone: clientData.phone,
        type: clientData.type, status: "active",
        assigned_to: clientData.assignedTo || null,
        sales_owner: clientData.salesOwner || null,
        state: clientData.state || null, cin: clientData.cin || null, gst: clientData.gst || null,
        incorporation_date: clientData.incorporationDate || null,
        custom_data: clientData.customData || {},
        total_billing: 0, collected: 0, pending: 0, progress: 0,
      });
      await sb.insert("users", {
        id: clientData.id, email: clientData.email?.toLowerCase(),
        phone: clientData.phone, name: clientData.contactName || clientData.name,
        role: "client", avatar: initials(clientData.contactName || clientData.name),
        color: "#7C3AED", client_no: clientData.clientNo,
      });
      showToast(`Client created! Login: ${clientData.email} / ${clientData.password}`, "success");
      // Supabase user_companies: links this portal login → Cosmos company (same id for primary contact)
      try {
        await sb.insert("user_companies", { user_id: clientData.id, company_id: clientData.id, role: "primary", is_primary: true });
      } catch (linkErr) {
        console.warn("user_companies insert:", linkErr.message);
      }
      // Supabase Auth identity (auth.users) ↔ app users row via auth_uid + app_user_id metadata
      const authRes = await createAuthFor({ email: clientData.email, phone: clientData.phone, password: clientData.password, appUserId: clientData.id, name: clientData.contactName || clientData.name, role: "client" });
      if (!authRes.ok) showToast("Client saved, but their login couldn't be created: " + (authRes.error || "use Reset Password to set it") + ".", "warning");
      const teamIds = [...new Set([clientData.assignedTo, ...staffUserIds(allUsers)].filter(Boolean))];
      if (teamIds.length) {
        notify(teamIds, {
          type: "client",
          title: "New client added",
          body: `${clientData.name} was added to the portal.`,
          meta: { clientId: clientData.id, modal: "clients" },
        });
      }
    } catch (e) {
      setClients(cs => cs.filter(c => c.id !== clientData.id));
      logAuditFailure("client_create_failed", "client", { id: clientData.id, label: clientData.name, companyId: clientData.id }, e.message || "Database or auth write failed");
      showToast(e.message || "Failed to save client — duplicate email, phone, or company name?", "error");
      throw e;
    }
  };
  const dbCreateInvoice = async (invoiceData, lineItems, schedule = null) => {
    setInvoices(is => [...is, invoiceData]); // optimistic
    const inCl = clients.find(c => c.id === invoiceData.clientId);
    const gstRate = Number(org?.gstRate) || 18;

    // One authoritative call: the server persists the invoice + line items,
    // AUTO-CREATES the tasks from each line item's task templates, and builds
    // the payment schedule. Nothing is hardcoded — tasks come from the data.
    const payload = {
      id: invoiceData.id,
      invoice_no: invoiceData.invoiceNo,
      company_id: invoiceData.clientId,
      client_name: invoiceData.clientName,
      date: invoiceData.date,
      due_date: invoiceData.dueDate || null,
      status: "unpaid",
      total: invoiceData.total, paid: 0, pending: invoiceData.total,
      letterhead: org,  // #G13 freeze the firm's branding/bank/GST at issue time
      assigned_to: inCl?.assignedTo || null,
      is_recurring: !!invoiceData.isRecurring,
      recurring_freq: invoiceData.recurringFreq || null,
      line_items: (lineItems || []).map((li, i) => ({
        name: li.name, type: li.type,         sac: li.sac || org?.sac || "",
        // HARDCODED SAC fallback removed: || "998211"
        qty: li.qty || 1, unit_price: li.unitPrice || 0, gst: !!li.gst,
        amount: li.amount != null ? li.amount : ((li.unitPrice || 0) * (li.qty || 1) * (li.gst ? (1 + gstRate / 100) : 1)),
        seq: i,
        director_count: li.qty || 1,
        tasks: Array.isArray(li.tasks) ? li.tasks : [],   // task templates → server expands
      })),
      schedule,
    };

    let created = {};
    try {
      created = await sb.createInvoice(payload);
    } catch (e) {
      console.error("createInvoice failed:", e.message);
      setInvoices(is => is.filter(i => i.id !== invoiceData.id)); // roll back optimistic
      showToast("Invoice could not be created — please retry.", "error");
      return;
    }

    const createdTasks = Array.isArray(created.tasks) ? created.tasks.map(mapTask) : [];
    if (createdTasks.length) setTasks(ts => [...ts, ...createdTasks]);

    const savedInv = normalizeInvoice({
      ...invoiceData,
      ...(created?.id ? mapInvoice(created) : {}),
      lineItems: invoiceData.lineItems || [],
    });
    setInvoices(is => {
      const next = [...is.filter(i => i.id !== invoiceData.id), savedInv];
      setClients(cs => cs.map(c => (
        c.id !== invoiceData.clientId ? c : { ...c, ...rollupClientBilling(c.id, next) }
      )));
      return next;
    });

    showToast(`Invoice ${invoiceData.invoiceNo} created${createdTasks.length ? ` with ${createdTasks.length} task${createdTasks.length !== 1 ? "s" : ""}` : ""}!`, "success");
    // #5 Notify the client a new invoice was raised (best-effort)
    if (inCl?.email) {
      const loginUrl = APP_PUBLIC_URL;
      const amt = `₹${Number(invoiceData.total || 0).toLocaleString("en-IN")}`;
      const html = `
        <div style="font-family:Arial,Helvetica,sans-serif;max-width:520px;margin:0 auto;color:#1f2937">
          <div style="background:#0B2545;padding:20px;border-radius:8px 8px 0 0;text-align:center">
            <h1 style="color:#fff;margin:0;font-size:18px">Founders Bridge</h1>
          </div>
          <div style="border:1px solid #e5e7eb;border-top:none;padding:22px;border-radius:0 0 8px 8px">
            <p>Hi ${inCl.contactName || inCl.name},</p>
            <p>A new invoice <strong>${invoiceData.invoiceNo}</strong> for <strong>${amt}</strong> has been raised on your account${invoiceData.dueDate ? `, due <strong>${invoiceData.dueDate}</strong>` : ""}.</p>
            <p style="margin:18px 0">
              <a href="${loginUrl}" style="background:#0B2545;color:#fff;text-decoration:none;padding:11px 20px;border-radius:6px;display:inline-block">View invoice</a>
            </p>
            <p style="font-size:13px;color:#6b7280">Log in to your portal to see the details and payment status.</p>
          </div>
        </div>`;
      fetch("/api/email/send", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ to: inCl.email, subject: `New invoice ${invoiceData.invoiceNo} — Founders Bridge`, html }),
      }).catch(() => { });
    }
    // In-app notification to client users (not company id)
    await notifyCompanyUsers(invoiceData.clientId, { userLinks, users: allUsers, clients }, {
      type: "invoice",
      title: `New invoice ${invoiceData.invoiceNo}`,
      body: `A new invoice for ₹${Number(invoiceData.total || 0).toLocaleString("en-IN")} has been raised on your account.`,
      meta: { invoiceId: invoiceData.id, modal: "view-invoice" },
    });
  };

  const dbCreateTicket = async (ticketData) => {
    setTickets(ts => [ticketData, ...ts]); // optimistic update
    const ticketMeta = { ticketId: ticketData.id, modal: "tickets" };
    // Notify counterparty: client raised → assignee; team raised → client users
    if (user?.role === "client") {
      if (ticketData.assignedTo) {
        notify(ticketData.assignedTo, {
          type: "ticket",
          title: "New client query",
          body: ticketData.subject || "",
          meta: ticketMeta,
        });
      }
    } else {
      await notifyCompanyUsers(ticketData.clientId, { userLinks, users: allUsers, clients }, {
        type: "ticket",
        title: "Update on your query",
        body: ticketData.subject || "",
        meta: ticketMeta,
      });
    }

    // Find company for client user
    let companyId = null;
    if (user?.role === "client") {
      const mine = clients.find(c =>
        c.email === user?.email || c.phone === user?.phone
      );
      companyId = mine?.id || (clients.length > 0 ? clients[0].id : null);
    } else {
      companyId = ticketData.clientId || null;
    }

    // Find assigned employee
    const assignedTo = companyId
      ? clients.find(c => c.id === companyId)?.assignedTo || employees[0]?.id || null
      : employees[0]?.id || null;

    const payload = {
      id: ticketData.id,
      ticket_no: ticketData.ticketNo,
      company_id: companyId,
      client_name: ticketData.clientName || user?.name || "",
      raised_by: ticketData.raisedBy || user?.id || null,
      assigned_to: assignedTo,
      subject: ticketData.subject,
      description: ticketData.description || "",
      priority: ticketData.priority || "medium",
      status: "open",
    };

    console.log("Inserting ticket:", payload);
    const result = await sb.insert("tickets", payload);

    if (result && result.length > 0) {
      console.log("✅ Ticket saved:", ticketData.ticketNo);
    } else {
      console.error("❌ Ticket insert failed — keeping in local state");
      // DB insert failed — keep optimistic ticket visible
      // DO NOT reload (would wipe local state)
      showToast("Ticket saved locally. Syncing to DB...", "warning");
      // Try one more time after 5s
      setTimeout(async () => {
        const retry = await sb.insert("tickets", payload);
        if (retry && retry.length > 0) {
          showToast("Ticket synced to DB", "success");
        }
      }, 5000);
    }
  };

  const dbAddTicketResponse = async (ticketId, resp) => {
    const ticket = tickets.find(t => t.id === ticketId);
    const respId = resp.id || uuid();
    const dated = { ...resp, id: respId, date: resp.date || today() };
    setTickets(ts => ts.map(t => t.id === ticketId ? {
      ...t, responses: [...t.responses, dated],
      status: resp.by === "team" ? "in_progress" : t.status,
      updatedAt: today(),
    } : t));
    await sb.insert("ticket_responses", {
      id: respId,
      ticket_id: ticketId, by_role: resp.by,
      by_name: user?.name || "", text: resp.text, date: dated.date,
      by_user_id: user?.id || null,
    });
    await sb.update("tickets", "id", ticketId, {
      status: resp.by === "team" ? "in_progress" : "open",
      updated_at: new Date().toISOString(),
    });
    // Team → client: server hook on ticket_responses insert. Client reply → assignee below.
    if (ticket && resp.by === "client" && ticket.assignedTo) {
      notify(ticket.assignedTo, {
        type: "ticket",
        title: "Client replied to ticket",
        body: `${ticket.clientName}: ${(resp.text || "").slice(0, 100)}`,
        meta: { ticketId, modal: "tickets" },
      });
    }
  };

  const dbSetTasks = async (updater) => {
    const newTasks = typeof updater === "function" ? updater(tasks) : updater;
    setTasks(newTasks);
    const changed = newTasks.filter(nt => {
      const old = tasks.find(t => t.id === nt.id);
      return old && (
        old.status !== nt.status ||
        old.notes !== nt.notes ||
        old.assignedTo !== nt.assignedTo ||
        old.dueDate !== nt.dueDate ||
        old.completedDate !== nt.completedDate
      );
    });
    for (const t of changed) {
      await sb.update("tasks", "id", t.id, {
        status: t.status,
        notes: t.notes || "",
        assigned_to: t.assignedTo || null,
        due_date: t.dueDate || null,
        completed_date: t.completedDate || null,
        updated_at: new Date().toISOString(),
      });
    }
  };

  const dbSetTickets = async (updater) => {
    const newTickets = typeof updater === "function" ? updater(tickets) : updater;
    setTickets(newTickets);
    const changed = newTickets.filter(nt => {
      const old = tickets.find(t => t.id === nt.id);
      return old && old.status !== nt.status;
    });
    for (const t of changed) await persistTicketUpdate(t);
  };

  const dbSetInvoices = async (updater) => {
    const newInvs = typeof updater === "function" ? updater(invoices) : updater;
    setInvoices(newInvs);
    const changed = newInvs.filter(ni => {
      const old = invoices.find(i => i.id === ni.id);
      return old && (old.paid !== ni.paid || old.status !== ni.status);
    });
    for (const inv of changed) {
      await sb.update("invoices", "id", inv.id, { paid: inv.paid, pending: inv.pending, status: inv.status });
    }
  };

  const dbSetSubs = async (updater) => {
    const newSubs = typeof updater === "function" ? updater(submissions) : updater;
    const added = newSubs.filter(s => !submissions.find(x => x.id === s.id));
    setSubs(newSubs);
    for (const sub of added) {
      await sb.insert("submissions", {
        id: sub.id, task_id: sub.taskId, company_id: sub.clientId,
        status: sub.status, form_data: sub.formData || {},
        documents: sub.documents || {}, note: sub.note || "",
        submitted_at: sub.submittedAt, locked: true,
      });
    }
    const changedSubs = newSubs.filter(ns => {
      const old = submissions.find(s => s.id === ns.id);
      return old && old.status !== ns.status;
    });
    for (const sub of changedSubs) {
      await sb.update("submissions", "id", sub.id, {
        status: sub.status, review_note: sub.reviewNote || "", reviewed_at: sub.reviewedAt || null,
      });
    }
    // Also update related task status
    const taskUpdates = newSubs.filter(ns => {
      const old = submissions.find(s => s.id === ns.id);
      return old && old.status !== ns.status;
    });
    for (const sub of taskUpdates) {
      const taskStatus = sub.status === "approved" ? "completed" : sub.status === "changes_requested" ? "client_action" : "team_approval";
      await sb.update("tasks", "id", sub.taskId, { status: taskStatus, updated_at: new Date().toISOString() });
    }
  };

  const dbSetPayments = async (updater) => {
    const newPmts = typeof updater === "function" ? updater(payments) : updater;
    const added = newPmts.filter(p => !payments.find(x => x.id === p.id));
    setPayments(newPmts);
    for (const p of added) {
      await sb.insert("payments", {
        id: p.id, invoice_id: p.invoiceId, company_id: p.clientId,
        client_name: p.clientName, amount: p.amount, mode: p.mode,
        reference: p.reference || "", date: p.date, notes: p.notes || "",
      });
    }
  };

  const dbSetDocTpls = async (updater) => {
    const nextTpls = typeof updater === "function" ? updater(docTpls) : updater;
    setDocTpls(nextTpls);
    try {
      // Persist via PUT /document-templates (bulk replace)
      await sb.saveDocTemplates(nextTpls);
    } catch (e) {
      // Legacy fallback via org_settings — commented hardcoding path kept for reference
      // try {
      //   const serialized = JSON.stringify(nextTpls);
      //   const updated = await sb.update("org_settings", "key", "document_templates", { value: serialized });
      //   if (!updated || updated.length === 0) {
      //     await sb.insert("org_settings", { key: "document_templates", value: serialized });
      //   }
      // } catch { }
      console.error("Failed to save document templates:", e.message);
    }
  };

  const dbSetOrg = async (newOrg) => {
    const fields = ["name", "email", "phone", "gstin", "pan", "sac", "address", "gstRate", "bankName", "accountNo", "ifsc", "upi", "logoUrl", "signature", "terms", "state"];
    const payload = {};
    for (const key of fields) {
      if (newOrg[key] !== undefined) payload[key] = newOrg[key];
    }
    setOrg(newOrg);
    try {
      const saved = await sb.updateOrganisationSettings(payload);
      setOrg(prev => ({ ...prev, ...saved, gstRate: Number(saved.gstRate) || prev.gstRate || 18 }));
      console.log("✅ Org settings saved via PUT /organisation/settings");
    } catch (e) {
      console.error("Failed to save org settings:", e.message);
      throw e;
    }
  };

  const notifBelongsToUser = (n) => !user?.id || !n.userId || n.userId === user.id;
  const unreadNotifs = notifications.filter(n => notifBelongsToUser(n) && !n.read).length;
  const myNotifs = notifications.filter(notifBelongsToUser).slice(0, 8);

  const dismissNotif = (id) => dismissNotification(id, setNotifs);

  const openNotifFromBell = (n) => {
    setNotifOpen(false);
    setNotifs(ns => ns.map(x => x.id === n.id ? { ...x, read: true } : x));
    sb.update("notifications", "id", n.id, { read: true }).catch(() => {});
    routeFromNotification(n, { setView, openModal, user, tasks, submissions, invoices, setNotifFocus });
  };

  const openNotifications = () => {
    if (isNativeApp()) {
      refreshNotifications(user?.id);
      setView("notifications");
      return;
    }
    setNotifOpen(o => !o);
  };

  useEffect(() => {
    if (!notifOpen) return;
    const onDoc = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [notifOpen]);

  // ─── Touch handlers (must be declared before any early return) ────────
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  if (isDeleteAccountPath(pathname)) {
    const leaveDeletePage = () => {
      try { window.history.replaceState({}, '', '/'); } catch (_) { /* ignore */ }
      setPathname('/');
      if (user) {
        const home = isClientPortalRole(user.role) ? "client-home" : ({ admin: "dashboard", manager: "dashboard", employee: "emp-dashboard" }[user.role] || "dashboard");
        setViewRaw(home);
      }
    };
    return (
      <>
        <DeleteAccountPage onBack={leaveDeletePage} />
        {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
      </>
    );
  }

  if (!user) {
    if (isPoliciesPath(pathname)) {
      return (
        <>
          <PolicyPage
            initialTab={policiesTabFromLocation()}
            onBack={() => {
              try { window.history.replaceState({}, '', '/'); } catch (_) { /* ignore */ }
              setPathname('/');
            }}
          />
          {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
        </>
      );
    }
    const onAuthCallback = window.location.pathname === '/auth/callback'
      || isOAuthCallbackUrl(window.location.href);
    if (onAuthCallback) {
      return (
        <>
          <AuthCallbackPage
            key={authCallbackKey}
            onLogin={handleLogin}
            onSignupProfile={() => setShowRegister(true)}
            showToast={showToast}
          />
          {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
          <SubmitOverlay label={submitOverlay?.label} step={submitOverlay?.step} />
        </>
      );
    }
    if (showRegister) return (
      <>
        <RegistrationPage
          onBack={() => {
            setShowRegister(false);
            try { window.history.replaceState({}, '', '/'); } catch (_) { /* ignore */ }
          }}
          onLogin={handleLogin}
          showToast={showToast}
        />
        {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
        <SubmitOverlay label={submitOverlay?.label} step={submitOverlay?.step} />
      </>
    );
    if (showForgot) return (
      <>
        <ForgotPasswordPage onBack={() => setShowForgot(false)} showToast={showToast} />
        {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
        <SubmitOverlay label={submitOverlay?.label} step={submitOverlay?.step} />
      </>
    );
    return (
      <>
        <LoginPageV2
          onLogin={handleLogin}
          showToast={showToast}
          onForgot={() => setShowForgot(true)}
          runSubmitting={runSubmitting}
        />
        {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
        <SubmitOverlay label={submitOverlay?.label} step={submitOverlay?.step} />
      </>
    );
  }

  // Permission checker — admin always full; anything unset defaults to allow (safe).
  const can = (module, action) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if (action === "delete" && user.role === "client") return false;
    const role = (appRoles || []).find(r => (r.name || "").toLowerCase() === (user.role || "").toLowerCase());
    if (role && role.permissions && role.permissions[module]) {
      return role.permissions[module][action] !== false;
    }
    const def = DEFAULT_ROLE_PERMISSIONS[user.role];
    if (def && def[module]) return def[module][action] !== false;
    if (isClientPortalRole(user.role)) return action === "view" || (action === "create" && (module === "tickets" || module === "marketplace"));
    return true;
  };

  // #12 Director logins — which companies the signed-in person can see
  const myCompanies = isClientPortalRole(user?.role)
    ? clients.filter(c =>
      userLinks.some(l => l.user_id === user.id && l.company_id === c.id) ||
      c.id === user.id)
    : user?.role === "employee"
      ? clients.filter(c => c.assignedTo === user.id)
      : clients;
  const visibleClients = user?.role === "employee" ? myCompanies : (isClientPortalRole(user?.role) ? myCompanies : clients);
  const activeCompany = activeCompanyId ? clients.find(c => c.id === activeCompanyId) : null;
  const visibleCompanyIds = activeCompany ? [activeCompany.id] : myCompanies.map(c => c.id);

  const ctx = {
    user, org, setOrg: dbSetOrg, bundles, setBundles, docTpls, setDocTpls: dbSetDocTpls,
    appRoles, can, paymentSchedule,
    myCompanies, visibleCompanyIds, visibleClients, activeCompanyId, setActiveCompanyId, userLinks, setUserLinks,
    clients, setClients, dbCreateClient,
    employees, setEmps,
    invoices, setInvoices: dbSetInvoices, dbCreateInvoice,
    tasks, setTasks: dbSetTasks,
    notifications, setNotifs, dismissNotif, notifFocus, setNotifFocus,
    payments, setPayments: dbSetPayments,
    submissions, setSubs: dbSetSubs,
    tickets, setTickets: dbSetTickets, dbCreateTicket, dbAddTicketResponse,
    crossSellOpps, setCrossSellOpps,
    users: allUsers, setAllUsers,
    showToast, runSubmitting, submitBusy: !!submitOverlay, openModal, closeModal, modal, view, setView,
    unreadNotifs, loadAll: () => loadAll(user), dbReady,
  };

  const NAV_V2 = {
    admin: [
      {
        sec: "Overview", items: [
          { id: "dashboard", label: "Dashboard" },
          { id: "delete-requests", label: "Pending Deletes" },
          { id: "analytics", label: "Analytics" },
          { id: "emp-analytics", label: "Employee Analytics" },
          { id: "billing-summary", label: "Billing Summary" },
          { id: "reports", label: "Reports" },
        ]
      },
      {
        sec: "Clients", items: [
          { id: "clients", label: "All Clients" },
          { id: "invoices", label: "Invoices" },
          { id: "payments", label: "Payments" },
          { id: "tasks", label: "Tasks" },
          { id: "recurring", label: "Recurring Tasks" },
          { id: "tickets", label: "Tickets" },
          { id: "marketplace", label: "Marketplace" },
          { id: "cross-sell", label: "Cross-Sell" },
        ]
      },
      {
        sec: "Resources", items: [
          { id: "free-gifts", label: "Free Resources" },
        ]
      },
      {
        sec: "Team", items: [
          { id: "employees", label: "Employees" },
          { id: "directors", label: "Directors" },
        ]
      },
      {
        sec: "Settings", items: [
          { id: "settings-org", label: "Organisation" },
          { id: "settings-bundles", label: "Bundles & Services" },
          { id: "settings-doctpls", label: "Document Templates" },
          { id: "settings-custom-fields", label: "Custom Fields" },
          { id: "settings-users", label: "Users & Roles" },
          { id: "settings-kraya", label: "WhatsApp (Kraya)" },
          { id: "settings-zoho", label: "Zoho WorkDrive" },
        ]
      },
    ],
    manager: [
      {
        sec: "Overview", items: [
          { id: "dashboard", label: "Dashboard" },
          { id: "delete-requests", label: "Pending Deletes" },
          { id: "emp-analytics", label: "Employee Analytics" },
          { id: "billing-summary", label: "Billing Summary" },
          { id: "reports", label: "Reports" },
        ]
      },
      {
        sec: "Clients", items: [
          { id: "clients", label: "All Clients" },
          { id: "invoices", label: "Invoices" },
          { id: "payments", label: "Payments" },
          { id: "tasks", label: "Tasks" },
          { id: "recurring", label: "Recurring Tasks" },
          { id: "tickets", label: "Tickets" },
          { id: "marketplace", label: "Marketplace" },
          { id: "cross-sell", label: "Cross-Sell" },
        ]
      },
      {
        sec: "Team", items: [
          { id: "employees", label: "Team" },
        ]
      },
      {
        sec: "Integrations", items: [
          { id: "settings-kraya", label: "WhatsApp (Kraya)" },
          { id: "settings-zoho", label: "Zoho WorkDrive" },
        ]
      },
    ],
    employee: [
      {
        sec: "Overview", items: [
          { id: "emp-dashboard", label: "Dashboard" },
          { id: "emp-analytics", label: "Analytics" },
          { id: "billing-summary", label: "Billing Summary" },
        ]
      },
      {
        sec: "My Work", items: [
          { id: "emp-clients", label: "My Clients" },
          { id: "invoices", label: "Invoices" },
          { id: "payments", label: "Payments" },
          { id: "emp-tasks", label: "My Tasks" },
          { id: "recurring", label: "Recurring Tasks" },
          { id: "tickets", label: "Tickets" },
          { id: "marketplace", label: "Marketplace" },
          { id: "cross-sell", label: "Cross-Sell" },
        ]
      },
      {
        sec: "Resources", items: [
          { id: "directors", label: "Directors" },
          { id: "settings-bundles", label: "Bundles & Services" },
          { id: "settings-doctpls", label: "Document Templates" },
        ]
      },
    ],
    client: [
      {
        sec: "My Portal", items: [
          { id: "client-home", label: "My Companies" },
          { id: "client-tasks", label: "My Tasks" },
          { id: "client-invoices", label: "My Billing" },
          { id: "client-docs", label: "Documents" },
          { id: "marketplace", label: "Services" },
          { id: "free-gifts", label: "Free Resources" },
          { id: "tickets", label: "Raise a Query" },
        ]
      },
    ],
  };

  const roleColor = { admin: "#7C3AED", manager: "#0369A1", employee: "#0F766E", client: "#2563EB" };
  const roleLabel = { admin: "Administrator", manager: "Manager", employee: "Employee", client: "Client" };
  const NAV_MODULE = { clients: "clients", invoices: "invoices", payments: "invoices", "billing-summary": "invoices", tasks: "tasks", recurring: "recurring_tasks", tickets: "tickets", marketplace: "marketplace", "cross-sell": "cross_sell", employees: "team_reports", reports: "team_reports", analytics: "team_reports", "emp-analytics": "team_reports" };
  const navRoleKey = isClientPortalRole(user.role) ? "client" : user.role;
  const baseNav = NAV_V2[navRoleKey] || NAV_V2.employee || [];
  const nav = baseNav
    .map(sec => ({ ...sec, items: sec.items.filter(it => { const m = NAV_MODULE[it.id]; return !m || can(m, "view"); }) }))
    .filter(sec => sec.items.length > 0);

  const TITLES = {
    dashboard: "Dashboard", "delete-requests": "Delete Requests Approval", analytics: "Analytics", reports: "Reports",
    "billing-summary": "Billing Summary",
    "emp-analytics": "Employee Analytics",
    clients: "Clients", invoices: "Invoices", payments: "Payments", tasks: "Tasks",
    recurring: "Recurring Tasks", tickets: "Tickets / Queries",
    marketplace: "Service Marketplace",
    "cross-sell": "Cross-Sell Opportunities",
    "free-gifts": "Free Resources",
    employees: "Employees",
    "settings-org": "Organisation Settings", "settings-bundles": "Bundles & Services",
    "settings-doctpls": "Document Templates",
    "settings-custom-fields": "Custom Fields",
    "settings-users": "Users & Roles", "settings-kraya": "WhatsApp Settings",
    "settings-zoho": "Zoho WorkDrive",
    directors: "Directors — Portal Logins",
    "emp-dashboard": "My Dashboard", "emp-tasks": "My Tasks", "emp-clients": "My Clients",
    "client-home": "My Companies", "client-tasks": "My Tasks",
    "client-invoices": "My Billing", "client-docs": "Documents",
    notifications: "Notifications", policies: "Policies",
  };

  const swipeBlocked = (target) => target?.closest?.(
    ".tbl-wrap, .modal-overlay, .notif-panel, .notif-backdrop, input, textarea, select, .client-checklist",
  );

  const handleTouchStart = (e) => {
    if (swipeBlocked(e.target)) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    if (!touchStartX.current) return;
    const diffX = e.touches[0].clientX - touchStartX.current;
    const diffY = e.touches[0].clientY - touchStartY.current;

    // Check if horizontal swipe is significant
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 40) {
      if (diffX > 0) {
        // Swipe left-to-right: open menu
        setMobileMenuOpen(true);
      } else {
        // Swipe right-to-left: close menu
        setMobileMenuOpen(false);
      }
      touchStartX.current = 0; // reset after triggering
    }
  };

  return (
    <AppCtx.Provider value={ctx}>
      <div
        className={`app ${PLATFORM_CLASS} role-${user.role}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      >
        {mobileMenuOpen && (
          <div className="sb-overlay" onClick={() => setMobileMenuOpen(false)} />
        )}

        {/* Sidebar */}
        <aside className={`sb ${mobileMenuOpen ? "mobile-open" : ""}`}>
          <div className="sb-logo">
            {org.logoUrl && !logoError ? (
              <>
                <img src={org.logoUrl} alt={org.name || "Logo"} className="sb-logo-img" onError={() => setLogoError(true)} />
                {org.name && <OrgBrandName name={org.name} compact />}
              </>
            ) : (
              <>
                <img src={getBrandLogoSrc()} alt={org.name || "Founders Bridge"} className="sb-logo-img" />
                {org.name && <OrgBrandName name={org.name} compact />}
              </>
            )}
            <div className="sb-tagline">Legal & Compliance Portal</div>
          </div>
          <div className="sb-role-pill">
            <div className="sb-role-name">{user.name}</div>
            <div className="sb-role-tag">{roleLabel[user.role]}</div>
          </div>
          <nav className="sb-nav">
            {nav.map(sec => (
              <div key={sec.sec}>
                <div className="sb-section">{sec.sec}</div>
                {sec.items.map(item => (
                  <div key={item.id} className={`sb-item ${view === item.id ? "on" : ""}`} onClick={() => { setView(item.id); setMobileMenuOpen(false); }}>
                    <span className="sb-icon">{getIcon(item.id)}</span>{item.label}
                    {item.badge > 0 && <span className="sb-badge">{item.badge}</span>}
                  </div>
                ))}
              </div>
            ))}
          </nav>
          <div className="sb-foot">
            <div className="sb-user">
              <div className="sb-av av" style={{ background: roleColor[user.role] }}>{user.avatar}</div>
              <div><div className="sb-uname">{user.name}</div><div className="sb-urole">{user.role}</div></div>
              <button type="button" className="sb-logout" onClick={logout}>Logout</button>
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              <button
                style={{ flex: 1, padding: "5px 8px", background: "rgba(255,255,255,.08)", border: "none", color: "rgba(255,255,255,.5)", fontSize: 10, borderRadius: 6, cursor: "pointer" }}
                onClick={() => openModal("change-password")}
              ><InlineIcon name="lock" size={12} gap={5}>Change Password</InlineIcon></button>
              <button
                style={{ flex: 1, padding: "5px 8px", background: "rgba(255,255,255,.08)", border: "none", color: "rgba(255,255,255,.5)", fontSize: 10, borderRadius: 6, cursor: "pointer" }}
                onClick={() => { setView("policies"); setMobileMenuOpen(false); }}
              ><InlineIcon name="clipboard" size={12} gap={5}>Policies</InlineIcon></button>
            </div>
            {user.role === "client" && (
            <button type="button" className="sb-delete-link" onClick={() => openModal("delete-account")}>
              Delete account…
            </button>
            )}
          </div>
        </aside>

        {/* Main */}
        <div className="main">
          <div className="topbar">
            <button className="menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <Icon name="close" size={20} /> : <Icon name="menu" size={20} />}
            </button>
            <div className="topbar-mobile-brand">{org.name || "Founders Bridge"}</div>
            <div className="topbar-title">{TITLES[view] || org.name || "Founders Bridge"}</div>
            {/* #12 Company switcher — only when a director has more than one company */}
            {isClientPortalRole(user.role) && myCompanies.length > 1 && (
              <select value={activeCompanyId || ""} onChange={e => setActiveCompanyId(e.target.value || null)}
                title="Switch company"
                style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid var(--border)", background: "#fff", fontSize: 12, fontWeight: 600, color: "var(--navy)", maxWidth: 220, marginRight: 8 }}>
                <option value="">All Companies</option>
                {myCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            {/* DB status */}
            {dbReady ? (
              <div className="topbar-live">
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--green)", display: "inline-block", animation: "pulse 2s ease infinite" }} />
                Live
              </div>
            ) : (
              <div className="topbar-db-warning" style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 10px", background: "#FFF8EC", border: "1px solid rgba(201,161,74,.4)", borderRadius: 6, fontSize: 11, color: "var(--gold-dk)", cursor: "pointer" }}
                onClick={() => loadAll(user)}>
                <InlineIcon name="warning" size={12} gap={5}>Demo mode · Retry</InlineIcon>
              </div>
            )}
            {/* Notification bell — desktop: dropdown; native app: full page */}
            <div ref={notifRef} className="topbar-notif-wrap">
              <button className="topbar-notification" aria-label="Open notifications" onClick={openNotifications}>
                <Icon name="bell" size={20} />
                {unreadNotifs > 0 && (
                  <span style={{ position: "absolute", top: -4, right: -4, width: 16, height: 16, borderRadius: "50%", background: "var(--red)", color: "#fff", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{unreadNotifs}</span>
                )}
              </button>
              {notifOpen && !isNativeApp() && (
                <>
                  <div className="notif-backdrop" onClick={() => setNotifOpen(false)} aria-hidden />
                  <div className="notif-panel" role="dialog" aria-label="Notifications">
                    <div className="notif-panel-head">
                      <span>Notifications</span>
                      <button type="button" className="btn btn-sm" onClick={() => { setNotifOpen(false); setView("notifications"); }}>View all</button>
                    </div>
                    {!myNotifs.length && <div className="notif-panel-empty">No notifications</div>}
                    {myNotifs.map(n => {
                      const vis = notificationVisual(n.type);
                      return (
                        <NotificationRow
                          key={n.id}
                          className={`notif-panel-item${n.read ? "" : " unread"}`}
                          onOpen={() => openNotifFromBell(n)}
                          onDismiss={() => dismissNotif(n.id)}
                        >
                          <div className="notif-panel-icon" style={{ background: vis.bg, color: vis.color }}>
                            <Icon name={vis.icon} size={14} />
                          </div>
                          <div className="notif-panel-text">
                            <div className="notif-panel-title">{n.title}</div>
                            <div className="notif-panel-body">{n.body}</div>
                          </div>
                        </NotificationRow>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <div className="topbar-org">{org.name}</div>
          </div>

          <div className="page">
            {!dbReady ? (
              <AppShellShimmer />
            ) : (
            <>
            {/* Cross-sell alerts for admin/manager */}
            {(user.role === "admin" || user.role === "manager") && view === "dashboard" &&
              clients.map(c => <CrossSellAlert key={c.id} client={c} tasks={tasks} />)
            }
            {view === "dashboard" && user.role === "manager" && <ManagerDashboard />}
            {view === "dashboard" && user.role === "admin" && <AdminDashboard />}
            {view === "analytics" && <AnalyticsPage />}
            {view === "emp-analytics" && <EmployeeAnalyticsPage />}
            {view === "billing-summary" && <BillingSummaryPage />}
            {view === "reports" && <ReportsPage clients={clients} invoices={invoices} tasks={tasks} employees={employees} payments={payments} />}
            {view === "clients" && <ClientsPage />}
            {view === "invoices" && <InvoicesPage />}
            {view === "payments" && <PaymentsPage payments={payments} invoices={invoices} clients={clients} />}
            {view === "tasks" && <TasksPage />}
            {view === "recurring" && <RecurringTasksPage />}
            {view === "tickets" && <TicketsPage />}
            {view === "marketplace" && <MarketplacePage />}
            {view === "cross-sell" && <CrossSellPage />}
            {view === "employees" && <EmployeesPage />}
            {view === "directors" && <DirectorsPage />}
            {view === "settings-org" && <OrgSettings />}
            {view === "settings-bundles" && <BundleSettings />}
            {view === "settings-doctpls" && <DocTemplatesSettings />}
            {view === "settings-custom-fields" && <CustomFieldsSettings />}
            {view === "settings-users" && <UserSettings />}
            {view === "settings-kraya" && <KrayaSettings showToast={showToast} />}
            {view === "settings-zoho" && <ZohoWorkdriveSettings showToast={showToast} />}
            {view === "emp-dashboard" && <EmpDashboard />}
            {view === "emp-tasks" && <EmpTasks />}
            {view === "emp-clients" && <EmpClients />}
            {view === "client-home" && <ClientHome />}
            {view === "client-tasks" && <ClientTasksV2 submissions={submissions} setSubs={setSubs} />}
            {view === "client-invoices" && <ClientInvoices />}
            {view === "client-docs" && <ClientDocs />}
            {view === "free-gifts" && <FreeGiftsPage />}
            {view === "delete-requests" && (user.role === "admin" || user.role === "manager") && (
              <DeleteRequestsPage user={user} sb={sb} users={allUsers} clients={clients} />
            )}
            {view === "notifications" && <NotificationsPage notifications={notifications} setNotifications={setNotifs} userId={user.id} />}
            {view === "policies" && <PolicyPage
              initialTab={policiesTabFromLocation()}
              onBack={() => setView(
              isClientPortalRole(user.role) ? "client-home" : user.role === "employee" ? "emp-dashboard" : "dashboard"
            )} />}
            </>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {modal?.id === "pay-now" && <PayNowModal data={modal.data} onClose={closeModal} invoices={invoices} setInvoices={setInvoices} payments={payments} setPayments={setPayments} clients={clients} showToast={showToast} />}
      {modal?.id === "create-client" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><CreateClientModal data={modal.data} onClose={closeModal} /></div>}
      {modal?.id === "edit-client" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><EditClientModal data={modal.data} onClose={closeModal} /></div>}
      {modal?.id === "create-invoice" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><CreateInvoiceModal data={modal.data} onClose={closeModal} /></div>}
      {modal?.id === "view-invoice" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><ViewInvoiceModal data={modal.data} onClose={closeModal} /></div>}
      {modal?.id === "view-document" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><ViewDocumentModal data={modal.data} onClose={closeModal} /></div>}
      {modal?.id === "view-task" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><ViewTaskModal data={modal.data} onClose={closeModal} /></div>}
      {modal?.id === "create-employee" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><CreateEmployeeModal data={modal.data} onClose={closeModal} /></div>}
      {modal?.id === "edit-employee" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><EditEmployeeModal data={modal.data} onClose={closeModal} /></div>}
      {modal?.id === "reset-password" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><ResetPasswordModal data={modal.data} onClose={closeModal} /></div>}
      {modal?.id === "record-payment" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><RecordPaymentModal data={modal.data} onClose={closeModal} invoices={invoices} setInvoices={setInvoices} payments={payments} setPayments={setPayments} clients={clients} setClients={setClients} showToast={showToast} /></div>}
      {modal?.id === "client-submit-task" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><ClientTaskSubmitModal data={modal.data} onClose={closeModal} submissions={submissions} setSubmissions={setSubs} tasks={tasks} setTasks={setTasks} showToast={showToast} /></div>}
      {modal?.id === "review-submission" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><ReviewSubmissionModal data={modal.data} onClose={closeModal} submissions={submissions} setSubmissions={setSubs} tasks={tasks} setTasks={setTasks} showToast={showToast} /></div>}
      {modal?.id === "add-task" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><AddTaskModal data={modal.data} onClose={closeModal} /></div>}
      {modal?.id === "edit-task" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><EditTaskModal data={modal.data} onClose={closeModal} /></div>}
      {modal?.id === "change-password" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><ChangePasswordModal onClose={closeModal} showToast={showToast} /></div>}
      {modal?.id === "force-change-password" && (
        <div className="modal-overlay">
          <ChangePasswordModal
            forced
            showToast={showToast}
            onClose={() => {}}
            onSuccess={() => {
              const updated = { ...user, mustChangePassword: false };
              localStorage.setItem("fb_user", JSON.stringify(updated));
              setUser(updated);
              closeModal();
              const defaultView = isClientPortalRole(updated.role) ? "client-home" : ({ admin: "dashboard", manager: "dashboard", employee: "emp-dashboard" }[updated.role] || "dashboard");
              setView(defaultView);
              loadAll(updated);
            }}
          />
        </div>
      )}
      {modal?.id === "delete-account" && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <DeleteAccountModal user={user} onClose={closeModal} showToast={showToast} onDeleted={logout} />
        </div>
      )}
      {modal?.id === "request-callback" && <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}><RequestCallbackModal onClose={closeModal} /></div>}

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
      <SubmitOverlay label={submitOverlay?.label} step={submitOverlay?.step} />
    </AppCtx.Provider>
  );
}


function AppWithBoundary() {
  return <ErrorBoundary><AppV2 /></ErrorBoundary>;
}

export { AppWithBoundary as default };
